'use client';

import { motion } from 'framer-motion';
import { Factory, type LucideIcon, Warehouse } from 'lucide-react';
import { fadeIn } from '@/lib/animations';

export interface Benefit {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export interface QuickStat {
  value: string;
  label: string;
}

const iconMap: Record<string, LucideIcon> = {
  Warehouse,
  Factory,
};

export interface WarehousingExcellenceProps {
  subtitle: string;
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  floatingStatValue: string;
  floatingStatLabel: string;
  contentIcon: string;
  contentTitle: string;
  contentParagraph1: React.ReactNode;
  contentParagraph2: React.ReactNode;
  quickStats: QuickStat[];
  whyChooseTitle: string;
  whyChooseDescription: string;
  benefits: Benefit[];
}

export function WarehousingExcellence({
  subtitle,
  title,
  description,
  imageUrl,
  imageAlt,
  floatingStatValue,
  floatingStatLabel,
  contentIcon,
  contentTitle,
  contentParagraph1,
  contentParagraph2,
  quickStats,
  whyChooseTitle,
  whyChooseDescription,
  benefits,
}: WarehousingExcellenceProps) {
  const ContentIcon = iconMap[contentIcon] || Warehouse;
  return (
    <section className="py-24 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 translate-y-48"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn('up')}
          className="text-center text-white mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-100 uppercase tracking-wider mb-2">
            {subtitle}
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold mb-6">
            {title}
          </h3>
          <p className="text-xl text-brand-100 max-w-4xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Image Side */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn('right')}
            className="relative"
          >
            <div className="absolute -top-8 -left-8 w-40 h-40 bg-white/10 rounded-3xl backdrop-blur-sm"></div>
            <img 
              src={imageUrl}
              alt={imageAlt} 
              className="rounded-2xl shadow-2xl relative z-10 w-full h-[500px] object-cover"
            />
            <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-brand-500/20 rounded-3xl backdrop-blur-sm"></div>
            
            {/* Floating Stats */}
            <div className="absolute top-8 right-8 bg-white/95 backdrop-blur-sm rounded-xl p-6 z-20 shadow-lg">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-600 mb-1">{floatingStatValue}</div>
                <div className="text-sm text-gray-600">{floatingStatLabel}</div>
              </div>
            </div>
          </motion.div>
          
          {/* Content Side */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn('left')}
            className="space-y-8"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <ContentIcon className="text-white" size={24} />
                </div>
                {contentTitle}
              </h4>
              <div className="text-lg text-brand-100 leading-relaxed mb-6">
                {contentParagraph1}
              </div>
              <div className="text-brand-100 leading-relaxed">
                {contentParagraph2}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-6">
              {quickStats.map((stat, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-sm text-brand-100">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Why Choose Section */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn('up')}
          className="text-center mb-12"
        >
          <h4 className="text-3xl font-bold text-white mb-4">
            {whyChooseTitle}
          </h4>
          <p className="text-brand-100 text-lg max-w-3xl mx-auto">
            {whyChooseDescription}
          </p>
        </motion.div>
        
        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn('up', 0.1 * (index + 1))}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/15 transition-all duration-300 group border border-white/20"
            >
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center text-white mx-auto mb-4 group-hover:bg-white/20 transition-colors group-hover:scale-110 transform duration-300">
                {benefit.icon}
              </div>
              <h5 className="text-lg font-bold text-white mb-3">{benefit.title}</h5>
              <p className="text-brand-100 text-sm leading-relaxed">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

